import React, { useContext, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import { useABeagleExperiments } from '@buzzfeed/react-components';
import { getUserUUID } from '@buzzfeed/bf-utils/lib/userid';
import { isEnhancedWebviewFeatureSupported } from '@buzzfeed/bf-utils/lib/webview';
import {
  isOn,
  getExperimentVariant,
} from '@buzzfeed/react-components/lib/utils/abeagle';
import { ABeagleContext, TrackingContext } from '../../contexts';
import abTests from '../../constants/ab-tests';
import { abeagleHost } from '../../constants';
import { useTrackingContext } from '../../hooks';

const SOURCE = 'island';
const DEFAULT_EXPERIMENT_STATE = {
  eligible: {},
  loaded: false,
  stale: false,
};

const ABeagleProvider = ({ children }) => {
  const router = useRouter();
  const trackingContextValue = useContext(TrackingContext) || {};
  const { trackExperimentActive } = useTrackingContext();
  const path = router?.asPath ? router.asPath.split('?')[0] : '';
  const returnedExperiments = useRef({ ...DEFAULT_EXPERIMENT_STATE, path: '' });

  const userId = useMemo(() => {
    if (typeof window !== 'undefined') {
      const isAbeagleSupported = isEnhancedWebviewFeatureSupported({
        feature: 'abeagle',
        version: 1,
      });
      if (isAbeagleSupported && window.BZFD_APP?.feature_support?.abeagle?.client_uuid) {
        // Note: this comes back in UPPER CASE, which is also how native
        //  app sends to abeagle - do NOT change.
        return window.BZFD_APP.feature_support.abeagle.client_uuid;
      }
      return getUserUUID();
    }
    return null;
  }, []);

  const memoizedArgs = useMemo(
    () => ({
      userId,
      data: { ...trackingContextValue },
    }),
    [userId, JSON.stringify(trackingContextValue)]
  );

  const experiments = useABeagleExperiments({
    abeagleHost:abeagleHost,
    experimentConfig: abTests,
    source: SOURCE,
    ...memoizedArgs,
  });

  if (
    !returnedExperiments.current ||
    returnedExperiments.current.loaded !== experiments.loaded
  ) {
    returnedExperiments.current = {
      ...experiments,
      path,
    };
  }

  returnedExperiments.current.stale = returnedExperiments.current.path !== path;
  const loaded = returnedExperiments.current.loaded;
  const eligibleExperiments = experiments?.eligible || {};
  const experimentKeys = Object.keys(eligibleExperiments).join('|');

  useEffect(() => {
    if (!loaded || !experimentKeys.length || typeof trackExperimentActive !== 'function') {
      return;
    }

    const experimentIds = [];
    Object.keys(eligibleExperiments).forEach((key) => {
      const experiment = eligibleExperiments[key];
      if (experiment && experiment.value) {
        const { id, version, value, variant_id: variantId } = experiment;
        experimentIds.push([key, id, version, value, variantId].join('|'));
      }
    });

    if (experimentIds.length) {
      trackExperimentActive({ experiment_id: experimentIds });
    }
  }, [eligibleExperiments, experimentKeys, loaded, trackExperimentActive]);

  const getFeatureFlagValue = (experimentName, onValue = 'on') =>
    isOn(returnedExperiments.current, experimentName, onValue);

  const getExperimentValue = (experimentName, options) =>
    getExperimentVariant(returnedExperiments.current, experimentName, options);

  return (
    <ABeagleContext.Provider
      value={{
        experiments: returnedExperiments.current,
        getFeatureFlagValue,
        getExperimentValue,
      }}
    >
      {children}
    </ABeagleContext.Provider>
  );
};

ABeagleProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
};

ABeagleProvider.defaultProps = {
  children: null,
};

export default ABeagleProvider;
